import React, { useEffect, useRef, useState } from 'react';
import './ApplyNow.css';

const SCRIPT_URL = process.env.REACT_APP_APPLY_SCRIPT_URL || '';

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="14" height="14">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const ApplyNowModal = ({ onClose, initialQuery = '' }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', course: '', query: initialQuery });
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Track touch to distinguish tap (close overlay) from swipe (ignore)
  const touchStartRef = useRef(null);
  const wasTouchRef = useRef(false);

  const handleOverlayTouchStart = (e) => {
    wasTouchRef.current = true;
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const handleOverlayTouchEnd = (e) => {
    if (!touchStartRef.current) return;
    const dx = Math.abs(e.changedTouches[0].clientX - touchStartRef.current.x);
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartRef.current.y);
    touchStartRef.current = null;
    // Only close on a clean tap — not a swipe in any direction
    if (dx < 8 && dy < 8) onClose();
  };

  // Prevent the synthetic onClick that fires after touchEnd on mobile
  const handleOverlayClick = () => {
    if (wasTouchRef.current) { wasTouchRef.current = false; return; }
    onClose();
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      setForm((prev) => ({ ...prev, phone: digits }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
    if (errors.length > 0) setErrors([]);
    if (submitError) setSubmitError('');
  };

  // Scroll focused field into view so mobile keyboard doesn't cover it
  const handleFocus = (e) => {
    e.target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const validate = () => {
    const missing = [];
    if (!form.name.trim()) missing.push('Name');
    if (!form.phone) missing.push('Phone');
    else if (form.phone.length !== 10) missing.push('Phone must be exactly 10 digits');
    if (!form.course.trim()) missing.push('Course');
    return missing;
  };

  const handleSubmit = async () => {
    const missing = validate();
    if (missing.length > 0) {
      setErrors(missing);
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await fetch(SCRIPT_URL, {
        method: 'POST',
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.replace(/\D/g, ''),
          email: form.email.trim(),
          course: form.course.trim(),
          query: form.query.trim(),
        }),
      });
      setSubmitted(true);
    } catch {
      setSubmitError('Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="an-overlay"
      onClick={handleOverlayClick}
      onTouchStart={handleOverlayTouchStart}
      onTouchEnd={handleOverlayTouchEnd}
    >
      <div
        className="an-container"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="an-title"
      >

        {/* ── SECTION 1: Sticky header ── */}
        <div className="an-header">
          <div>
            <h2 className="an-title" id="an-title">Apply Now</h2>
            <p className="an-subtitle">Let us help you find the right college.</p>
          </div>
          <button className="an-close" onClick={onClose} aria-label="Close Apply Now form">
            <CloseIcon />
          </button>
        </div>

        {submitted ? (
          /* Success — no footer needed */
          <div className="an-body an-body--success">
            <div className="an-success">
              <div className="an-success__icon">✓</div>
              <h3 className="an-success__title">Application Received!</h3>
              <p className="an-success__msg">
                Thank you, <strong>{form.name}</strong>! Our team will reach out to you on{' '}
                <strong>{form.phone}</strong> shortly.
              </p>
              <button className="an-btn an-btn--close" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
          <>
            {/* ── SECTION 2: Scrollable form fields ── */}
            <div className="an-body">
              <form className="an-form" onSubmit={(e) => e.preventDefault()}>

                {/* Row 1: Name + Phone */}
                <div className="an-row">
                  <div className="an-field">
                    <label htmlFor="an-name">
                      Name <span className="an-required">*</span>
                    </label>
                    <input
                      id="an-name"
                      name="name"
                      type="text"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      className={errors.includes('Name') ? 'an-input--error' : ''}
                    />
                  </div>
                  <div className="an-field">
                    <label htmlFor="an-phone">
                      Phone <span className="an-required">*</span>
                    </label>
                    <input
                      id="an-phone"
                      name="phone"
                      type="tel"
                      placeholder="10-digit mobile number"
                      value={form.phone}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      maxLength={10}
                      className={errors.some((err) => err.startsWith('Phone')) ? 'an-input--error' : ''}
                    />
                    <span className={`an-counter ${form.phone.length === 10 ? 'an-counter--ok' : form.phone.length > 0 ? 'an-counter--warn' : ''}`}>
                      {form.phone.length}/10
                    </span>
                  </div>
                </div>

                {/* Row 2: Email + Course */}
                <div className="an-row">
                  <div className="an-field">
                    <label htmlFor="an-email">
                      Email <span className="an-optional">(optional)</span>
                    </label>
                    <input
                      id="an-email"
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      value={form.email}
                      onChange={handleChange}
                      onFocus={handleFocus}
                    />
                  </div>
                  <div className="an-field">
                    <label htmlFor="an-course">
                      Course <span className="an-required">*</span>
                    </label>
                    <input
                      id="an-course"
                      name="course"
                      type="text"
                      placeholder="e.g. B.Tech, MBA, Law..."
                      value={form.course}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      maxLength={30}
                      className={errors.includes('Course') ? 'an-input--error' : ''}
                    />
                    <span className={`an-counter ${form.course.length === 30 ? 'an-counter--warn' : ''}`}>
                      {form.course.length}/30
                    </span>
                  </div>
                </div>

                {/* Row 3: Query full-width */}
                <div className="an-field">
                  <label htmlFor="an-query">
                    Your Query <span className="an-optional">(optional)</span>
                  </label>
                  <textarea
                    id="an-query"
                    name="query"
                    rows={5}
                    placeholder="Any specific questions or requirements..."
                    value={form.query}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    maxLength={150}
                  />
                  <span className={`an-counter ${form.query.length === 150 ? 'an-counter--warn' : ''}`}>
                    {form.query.length}/150
                  </span>
                </div>

              </form>
            </div>

            {/* ── SECTION 3: Sticky footer — errors + submit ── */}
            <div className="an-footer">
              {errors.length > 0 && (
                <div className="an-errors" role="alert">
                  Please fill in: {errors.join(', ')}
                </div>
              )}
              {submitError && (
                <div className="an-errors" role="alert">{submitError}</div>
              )}
              <button
                type="button"
                className="an-btn an-btn--submit"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Submitting…' : 'Apply'}
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default ApplyNowModal;
